import { parseMeta } from './parse_meta';
import { UserStore } from '@reverbdotcom/discovery-ui';

const ZD_WIDGET_ROLLOUT = 'new_zd_widget';
const HELP_CHAT_WINDOW_STATUS_LOCAL_STORAGE_KEY = 'helpChatWindowStatus';
const MINIMIZED_WINDOW_STATUS = 'minimized';
const OPEN_WINDOW_STATUS = 'open';

function loadZendeskSettings(chatDepartmentId, tags) {
  window.zESettings = {
    webWidget: {
      chat: {
        tags,
        departments: {
          enabled: [],
          select: chatDepartmentId,
        },
        prechatForm: {
          greeting: {
            '*': I18n.t('zendesk.zopim.prechatForm.greetings'),
          },
        },
        title: {
          '*': I18n.t('zendesk.zopim.window.title'),
        },
      },
      color: {
        bypassAccessibilityRequirement: true,
      },
      helpCenter: {
        chatButton: {
          '*': I18n.t('zendesk.webWidget.helpCenter.chatButton'),
        },
        title: {
          '*': I18n.t('zendesk.webWidget.helpCenter.title'),
        },
        searchPlaceholder: {
          '*': I18n.t('zendesk.webWidget.helpCenter.searchPlaceholder'),
        },
      },
      launcher: {
        chatLabel: {
          '*': I18n.t('zendesk.webWidget.launcher.chatLabel'),
        },
        label: {
          '*': I18n.t('zendesk.webWidget.launcher.label'),
        },
      },
      contactOptions: {
        contactButton: {
          '*': I18n.t('zendesk.webWidget.contactOptions.contactButton'),
        },
      },
    },
  };
}

export function getChatWindowStatusFromLocalStorage() {
  try {
    return localStorage.getItem(HELP_CHAT_WINDOW_STATUS_LOCAL_STORAGE_KEY);
  } catch (e) {
    console.log(e);
  }

  return null;
}

export function hasPersistedOpenChat() {
  const windowStatusFromLocalStorage = getChatWindowStatusFromLocalStorage();

  return windowStatusFromLocalStorage === OPEN_WINDOW_STATUS;
}

function setChatWindowStatus(newStatus) {
  try {
    if (newStatus) {
      localStorage.setItem(HELP_CHAT_WINDOW_STATUS_LOCAL_STORAGE_KEY, newStatus);
    } else {
      localStorage.removeItem(HELP_CHAT_WINDOW_STATUS_LOCAL_STORAGE_KEY);
    }
  } catch (e) {
    console.log(e);
  }
}

/* eslint-disable */
function loadLegacyWidget() {
  window.zEmbed || function (e, t) { var n, o, d, i, s, a = [], r = document.createElement("iframe"); window.zEmbed = function () { a.push(arguments) }, window.zE = window.zE || window.zEmbed, r.src = "javascript:false", r.title = "", r.role = "presentation", (r.frameElement || r).style.cssText = "display: none", d = document.getElementsByTagName("script"), d = d[d.length - 1], d.parentNode.insertBefore(r, d), i = r.contentWindow, s = i.document; try { o = s } catch (e) { n = document.domain, r.src = 'javascript:var d=document.open();d.domain="' + n + '";void(0);', o = s } o.open()._l = function () { var e = this.createElement("script"); n && (this.domain = n), e.id = "js-iframe-async", e.src = "https://assets.zendesk.com/embeddable_framework/main.js", this.t = +new Date, this.zendeskHost = "reverbdotcom.zendesk.com", this.zEQueue = a, this.body.appendChild(e) }, o.write('<body onload="document._l();">'), o.close() }();
}
/* eslint-enable */


function loadWidget(key) {
  const el = document.createElement('script');
  el.id = 'ze-snippet';
  el.type = 'text/javascript';
  el.async = true;
  el.src = `https://static.zdassets.com/ekr/snippet.js?key=${key}`;
  document.body.appendChild(el);

  return new Promise((res, rej) => {
    el.onload = res;
    el.onerror = rej;
  });
}

function getWidgetConfig() {
  // set in _zendesk_embed.haml.
  // this should probably be moved into common state like UserContext

  return window.__zendeskWidgetConfig;
}

/**
 * Opens the Zendesk widget. Must be called after `loadZendeskEmbed` resolves
 */
export function openWidget() {
  window.zE('webWidget', 'open');
}

function configureZopim(config) {
  return new Promise((res, rej) => {
    window.zE(() => {
      try {
        // set widget locale
        window.zE.setLocale(config.zendeskAllowedLocale);

        // set search suggestions
        window.zE.setHelpCenterSuggestions({
          labels: config.searchLabels,
        });
      } catch (e) {
        rej(e);
      }

      window.$zopim(() => {
        const meta = parseMeta('current-user');

        if (meta.user.name) {
          window.$zopim.livechat.setName(meta.user.name);
          window.$zopim.livechat.setEmail(meta.user.email);
        }

        window.$zopim.livechat.departments.filter(''); // removes department selection dropdown
        window.$zopim.livechat.departments.setVisitorDepartment(config.chatDepartmentId);
        window.$zopim.livechat.addTags(config.tags);

        window.$zopim.livechat.window.setTitle(I18n.t('zendesk.zopim.window.title'));

        window.$zopim.livechat.prechatForm.setGreetings(I18n.t('zendesk.zopim.prechatForm.greetings'));

        // Finish chat-specific setup
        window.$zopim.livechat.setOnConnected(() => {
          // this cannot be run on load or it will not work
          // wait until we are connected, then set the size
          window.$zopim.livechat.window.setSize('small');

          // zopim has an onShow callback, but because we are wrapping the zopim chat window
          // it is not fired when the window is maximized.
          // https://api.zopim.com/files/meshim/widget/controllers/liveChatAPI/Window-js.html#$zopim.livechat.window.onShow
          // To get onShow-eque behavior, we will monkeypatch the normal window.show function here
          const oldShow = window.$zopim.livechat.window.show;
          window.$zopim.livechat.window.show = () => {
            setChatWindowStatus(OPEN_WINDOW_STATUS);
            return oldShow.apply(this);
          };

          window.$zopim.livechat.window.onShow(() => {
            setChatWindowStatus(OPEN_WINDOW_STATUS);
          });


          window.$zopim.livechat.window.onHide(() => {
            setChatWindowStatus(MINIMIZED_WINDOW_STATUS);
          });

          window.$zopim.livechat.setOnChatEnd(() => {
            setChatWindowStatus();
          });

          window.$zopim.livechat.setOnChatStart(() => {
            setChatWindowStatus(OPEN_WINDOW_STATUS);
          });

          res();
        });
      });
    });
  });
}

export function loadZendeskEmbed() {
  if (!ZENDESK_WIDGET_KEY) {
    return Promise.reject('ZENDESK_WIDGET_KEY is unset, not loading');
  }

  const config = getWidgetConfig();
  loadZendeskSettings(config.chatDepartmentId, config.tags);

  if (!UserStore.isExperimentEnabled(ZD_WIDGET_ROLLOUT)) {
    loadLegacyWidget();
    return configureZopim(config);
  }

  return loadWidget(ZENDESK_WIDGET_KEY).then(() => {
    // set widget locale
    window.zE('webWidget', 'setLocale', config.zendeskAllowedLocale);

    // set search suggestions
    window.zE('webWidget', 'helpCenter:setSuggestions', {
      labels: config.searchLabels,
    });

    const currentUser = parseMeta('current-user');
    if (currentUser.user.name) {
      window.zE('webWidget', 'prefill', {
        name: { value: currentUser.user.name, readOnly: false },
        email: { value: currentUser.user.email, readOnly: false },
      });
    }

    window.zE('webWidget:on', 'close', () => {
      setChatWindowStatus(MINIMIZED_WINDOW_STATUS);
    });

    window.zE('webWidget:on', 'chat:end', () => {
      setChatWindowStatus();
    });

    window.zE('webWidget:on', 'chat:start', () => {
      setChatWindowStatus(OPEN_WINDOW_STATUS);
    });

    return new Promise((res) => {
      window.zE('webWidget:on', 'chat:connected', res);
    });
  });
}



// WEBPACK FOOTER //
// ./javascripts/components/zendesk.js